import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import logger from '../utils/logging';
import productsService from '../services/productsService';
import PWAInstallPrompt from '../components/features/PWAInstallPrompt';
import PWAInstallButton from '../components/features/PWAInstallButton';
import PLACEHOLDER_IMAGE from '../utils/placeholderImage';

// Morphing Shapes Component
const MorphingShapes = ({ isMobile }) => {
  const [shapes, setShapes] = useState(() => {
    return Array.from({ length: 6 }).map((_, i) => ({
      id: i,
      left: 10 + (i * 15) % 80,
      top: 10 + (i * 20) % 80,
      opacity: 0.3
    }));
  });

  const timeoutsRef = useRef([]);

  useEffect(() => {
    const initialShapes = Array.from({ length: 6 }).map((_, i) => i);

    initialShapes.forEach((shapeId) => {
      const scheduleNext = () => {
        // Random delay before fading out (3-8 seconds)
        const fadeOutDelay = 3000 + Math.random() * 5000;
        
        const fadeOutTimeout = setTimeout(() => {
          // Fade out
          setShapes((prevShapes) => {
            return prevShapes.map((s) => {
              if (s.id === shapeId) {
                return { ...s, opacity: 0 };
              }
              return s;
            });
          });

          // After fade out completes (0.8s), move and fade in
          const moveAndFadeInTimeout = setTimeout(() => {
            setShapes((prevShapes) => {
              return prevShapes.map((s) => {
                if (s.id === shapeId) {
                  return {
                    ...s,
                    left: 10 + Math.random() * 80,
                    top: 10 + Math.random() * 80,
                    opacity: 0.3
                  };
                }
                return s;
              });
            });

            // Schedule the next cycle for this shape
            scheduleNext();
          }, 800); // Wait for fade out transition to complete

          timeoutsRef.current.push(moveAndFadeInTimeout);
        }, fadeOutDelay);

        timeoutsRef.current.push(fadeOutTimeout);
      };

      // Start the cycle for this shape
      scheduleNext();
    });

    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const size = isMobile ? '60px' : '100px';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1,
      pointerEvents: 'none',
      overflow: 'hidden'
    }}>
      {shapes.map((shape) => (
        <div
          key={shape.id}
          style={{
            position: 'absolute',
            left: `${shape.left}%`,
            top: `${shape.top}%`,
            width: size,
            height: size,
            maxWidth: isMobile ? '60px' : '100px',
            maxHeight: isMobile ? '60px' : '100px',
            borderRadius: '50%',
            background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
            animation: `morph 8s ease-in-out infinite`,
            animationDelay: `${shape.id * 1.5}s`,
            opacity: shape.opacity,
            transform: 'translate(-50%, -50%)',
            transition: 'opacity 0.8s ease-in-out, left 0s linear 0.8s, top 0s linear 0.8s'
          }}
        />
      ))}
    </div>
  );
};

const Home = () => {
  const canvasRef = useRef(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // Handle responsive breakpoint
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    logger.logComponentMount('Home', {
      timestamp: Date.now(),
      hasCanvas: !!canvasRef.current
    });
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    try {
      setLoading(true);
      console.log('🏠 [Home] Loading featured products...');
      const allProducts = await productsService.getProducts();
      console.log('🏠 [Home] All products loaded:', allProducts.length);
      // Get first 3 products as featured
      const featured = allProducts.slice(0, 3);
      setFeaturedProducts(featured);
      console.log('🏠 [Home] Featured products set:', featured.length);
      console.log('🏠 [Home] Featured products:', featured.map(p => p.name));
    } catch (error) {
      console.error('❌ [Home] Failed to load featured products:', error);
    } finally {
      setLoading(false);
    }
  };

    // Matrix Rain Effect
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        
        const matrix = "ABCDEFGHIJKLMNOPQRSTUVWXYZ123456789@#$%^&*()*&^%+-/~{[|`]}";
        const matrixArray = matrix.split("");
        const fontSize = 10;
        const columns = canvas.width / fontSize;
        const drops = [];
        
        for (let x = 0; x < columns; x++) {
            drops[x] = 1;
        }
        
        let hue = 0;
        let bgHue = 180;
        
        function draw() {
            ctx.fillStyle = `hsla(${bgHue}, 70%, 5%, 0.1)`;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            bgHue = (bgHue + 0.5) % 360;
            
            for (let i = 0; i < drops.length; i++) {
                const text = matrixArray[Math.floor(Math.random() * matrixArray.length)];
                const columnHue = (hue + i * 10) % 360;
                ctx.fillStyle = `hsl(${columnHue}, 100%, 60%)`;
                ctx.font = fontSize + 'px monospace';
                ctx.fillText(text, i * fontSize, drops[i] * fontSize);
                
                if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
                    drops[i] = 0;
                }
                drops[i]++;
            }
            hue = (hue + 2) % 360;
        }
        
        const interval = setInterval(draw, 35);
        return () => clearInterval(interval);
    }, []);

    return (
        <div style={{
            minHeight: '100vh',
            position: 'relative',
            background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
            overflow: 'auto'
        }}>
            {/* Matrix Rain */}
            <canvas 
                ref={canvasRef}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 0,
                    pointerEvents: 'none'
                }}
            />

            {/* Floating Particles */}
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1,
                pointerEvents: 'none'
            }}>
                {Array.from({ length: 80 }).map((_, i) => (
                    <div
                        key={i}
                        style={{
                            position: 'absolute',
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            width: `${Math.random() * 8 + 3}px`,
                            height: `${Math.random() * 8 + 3}px`,
                            background: `hsl(${Math.random() * 360}, 80%, 60%)`,
                            borderRadius: '50%',
                            boxShadow: `0 0 ${Math.random() * 30 + 15}px currentColor`,
                            animation: `float ${Math.random() * 10 + 5}s ease-in-out infinite`,
                            animationDelay: `${Math.random() * 5}s`,
                            opacity: 0.7
                        }}
                    />
                ))}
            </div>

            {/* Morphing Shapes */}
            <MorphingShapes isMobile={isMobile} />


            {/* Hero Section */}
            <section style={{
                position: 'relative',
                color: 'white',
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                paddingTop: '20px',
                paddingBottom: '80px'
            }}>
                <div style={{
                    position: 'relative',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '0 16px',
                    textAlign: 'center',
                    zIndex: 10,
                    width: '100%',
                    boxSizing: 'border-box'
                }}>
                    {/* Main Heading */}
                    <h1 style={{
                        fontSize: 'clamp(3rem, 6vw, 5rem)',
                        fontWeight: 'bold',
                        marginBottom: '32px',
                        color: 'white',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                    }}>
                        <span style={{ display: 'block' }}>Welcome to</span>
                        <span style={{ display: 'block', marginTop: '8px' }}>
                            <span style={{
                                padding: '12px 24px',
                                borderRadius: '12px',
                                whiteSpace: 'nowrap',
                                background: 'linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                            }}>
                                Rainbow Sails
                            </span>
                        </span>
                    </h1>
                    
                    {/* Techno Artist AI on LSD */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '32px',
                        gap: '16px'
                    }}>
                        <span style={{
                            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                            fontWeight: 'bold',
                            background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                        }}>
                            Techno Artist
                        </span>
                        <span style={{ fontSize: '3rem', animation: 'bounce 2s infinite' }}>🍄</span>
                        <span style={{
                            fontSize: 'clamp(2rem, 4vw, 3rem)',
                            fontWeight: 'bold',
                            background: 'linear-gradient(135deg, #10b981, #3b82f6)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                        }}>
                            AI on LSD
                        </span>
                    </div>
                    
                    {/* Welcome Message */}
                    <p style={{
                        fontSize: 'clamp(1.25rem, 2.5vw, 2rem)',
                        marginBottom: '40px',
                        color: 'white',
                        maxWidth: '800px',
                        margin: '0 auto 40px',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                        lineHeight: '1.6'
                    }}>
                        <span style={{ color: '#06b6d4', fontWeight: '600' }}>Step into a world where</span> AI meets consciousness and art becomes reality. 
                        Zee Sails has created something extraordinary - digital art that's literally 
                        <span style={{ color: '#fbbf24', fontWeight: 'bold' }}> HIGH ON CREATIVITY</span> and ready to expand your mind.
                    </p>
                    
                    {/* Featured Products */}
                    <div style={{ marginBottom: '40px' }}>
                        <h2 style={{
                            textAlign: 'center',
                            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                            fontWeight: 'bold',
                            color: 'white',
                            marginBottom: '24px',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                        }}>
                            Featured Products
                        </h2>
                        {loading ? (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '200px',
                                color: 'white',
                                fontSize: '18px'
                            }}>
                                Loading featured products...
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '24px',
                                maxWidth: '900px',
                                margin: '0 auto',
                                padding: '0 16px'
                            }}>
                                {/* First Row - 2 Products */}
                                <div style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '16px',
                                    justifyContent: 'center',
                                    width: '100%'
                                }}>
                                    {featuredProducts.slice(0, 2).map((product, index) => (
                                    <Link
                                        key={product.id}
                                        to={`/product/${product.name.toLowerCase().replace(/\s+/g, '-')}`}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            maxWidth: '280px',
                                            minWidth: '200px',
                                            aspectRatio: '1',
                                            overflow: 'hidden',
                                            borderRadius: '12px',
                                            background: 'rgba(0, 0, 0, 0.8)',
                                            backdropFilter: 'blur(4px)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            boxShadow: '0 25px 50px rgba(0,0,0,0.8)',
                                            flexShrink: 0,
                                            textDecoration: 'none',
                                            transition: 'transform 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <img 
                                            src={product.primaryImage || product.customImage || product.images?.[0] || PLACEHOLDER_IMAGE}
                                            alt={product.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                                console.error('❌ [Home] Image failed to load:', e.target.src);
                                                // Fallback chain for Mango products
                                                if (product.images && product.images.length > 1) {
                                                  const currentIndex = product.images.indexOf(e.target.src);
                                                  if (currentIndex !== -1 && currentIndex < product.images.length - 1) {
                                                    e.target.src = product.images[currentIndex + 1];
                                                    return;
                                                  }
                                                }
                                                // Use placeholder constant instead of hiding
                                                if (e.target.src !== PLACEHOLDER_IMAGE) {
                                                  e.target.src = PLACEHOLDER_IMAGE;
                                                }
                                            }}
                                        />
                                    </Link>
                                    ))}
                                </div>
                                
                                {/* Second Row - 3rd Product Centered */}
                                {featuredProducts.length > 2 && (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        width: '100%'
                                    }}>
                                        {featuredProducts.slice(2, 3).map((product, index) => (
                                            <Link
                                                key={product.id}
                                                to={`/product/${product.name.toLowerCase().replace(/\s+/g, '-')}`}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    maxWidth: '280px',
                                                    minWidth: '200px',
                                                    aspectRatio: '1',
                                                    overflow: 'hidden',
                                                    borderRadius: '12px',
                                                    background: 'rgba(0, 0, 0, 0.8)',
                                                    border: '2px solid rgba(236, 72, 153, 0.3)',
                                                    flexShrink: 0,
                                                    textDecoration: 'none',
                                                    transition: 'transform 0.3s ease'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                            >
                                                <img 
                                                    src={product.primaryImage || product.customImage || product.images?.[0] || PLACEHOLDER_IMAGE}
                                                    alt={product.name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: white; font-size: 18px; font-weight: bold;">' + product.name + '</div>';
                                                    }}
                                                />
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {/* Main CTA Button */}
                    <div style={{ marginBottom: '40px' }}>
                        <Link
                            to="/gallery"
                            style={{
                                background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                                color: 'white',
                                padding: '16px 40px',
                                borderRadius: '50px',
                                fontWeight: '600',
                                fontSize: '20px',
                                textDecoration: 'none',
                                transition: 'all 0.3s ease',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '12px',
                                boxShadow: '0 25px 50px rgba(0,0,0,0.8)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = 'linear-gradient(135deg, #db2777, #7c3aed)';
                                e.target.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'linear-gradient(135deg, #ec4899, #8b5cf6)';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            <span>Enter the Gallery</span>
                            <ArrowRight size={24} />
                        </Link>
                    </div>
                    
                    {/* Experience Text */}
                    <p style={{
                        fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                        marginBottom: '32px',
                        color: '#d1d5db',
                        maxWidth: '600px',
                        margin: '0 auto 32px',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                    }}>
                        <span style={{ color: '#06b6d4', fontWeight: '600' }}>Experience:</span> Art that transcends reality and opens new dimensions of consciousness. 
                        Each piece contains <span style={{ color: '#8b5cf6', fontWeight: 'bold' }}>NEURAL PATHWAYS</span> that will expand your mind 
                        to see colors and patterns beyond this dimension.
                    </p>
                    
                    <p style={{
                        fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                        marginBottom: '32px',
                        color: '#f3e8ff',
                        maxWidth: '600px',
                        margin: '0 auto 32px',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                        fontWeight: '600'
                    }}>
                        ✨ <span style={{ color: '#fbbf24' }}>98.7% of viewers</span> report experiencing <span style={{ color: '#06b6d4' }}>mind expansion</span> 
                        and <span style={{ color: '#8b5cf6' }}>consciousness shifts</span> within moments of viewing. 
                        <span style={{ color: '#10b981' }}> Ready to explore?</span>
                    </p>

                    {/* About Link */}
                    <div style={{ marginTop: '32px' }}>
                        <Link
                            to="/about"
                            style={{
                                color: '#d1d5db',
                                textDecoration: 'none',
                                transition: 'color 0.3s ease',
                                fontSize: '18px'
                            }}
                            onMouseEnter={(e) => e.target.style.color = '#ec4899'}
                            onMouseLeave={(e) => e.target.style.color = '#d1d5db'}
                        >
                            Meet the AI Shaman →
                        </Link>
                    </div>
                </div>
            </section>

            {/* Simple Call to Action Section */}
            <section style={{
                padding: '64px 0',
                color: 'white',
                position: 'relative',
                zIndex: 50
            }}>
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    padding: '0 16px',
                    textAlign: 'center',
                    position: 'relative',
                    zIndex: 50
                }}>
                    <h2 style={{
                        fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                        fontWeight: 'bold',
                        marginBottom: '16px',
                        color: 'white',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                    }}>
                        Ready to explore consciousness through art?
                    </h2>
                    <p style={{
                        fontSize: '18px',
                        color: '#d1d5db',
                        maxWidth: '500px',
                        margin: '0 auto 32px',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                    }}>
                        Step into a world where AI meets creativity and reality bends to imagination.
                    </p>

                    <Link
                        to="/gallery"
                        style={{
                            background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                            color: 'white',
                            padding: '16px 40px',
                            borderRadius: '50px',
                            fontWeight: '600',
                            fontSize: '20px',
                            textDecoration: 'none',
                            transition: 'all 0.3s ease',
                            display: 'inline-block',
                            boxShadow: '0 25px 50px rgba(0,0,0,0.8)'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #db2777, #7c3aed)';
                            e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #ec4899, #8b5cf6)';
                            e.target.style.transform = 'scale(1)';
                        }}
                    >
                        Explore the Gallery
                    </Link>
                    
                    {/* PWA Install Button - Part of page content */}
                    <PWAInstallButton />
                </div>
            </section>

            {/* PWA Install Prompt (dismissible popup) */}
            <PWAInstallPrompt />

            {/* CSS Animations */}
            <style>{`
                @keyframes glow {
                    0%, 100% { 
                        box-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
                    }
                    50% { 
                        box-shadow: 0 0 40px rgba(0, 255, 255, 0.8);
                    }
                }
                
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }
                
                @keyframes morph {
                    0%, 100% { 
                        transform: scale(1) rotate(0deg); 
                        border-radius: 50%;
                    }
                    25% { 
                        transform: scale(1.2) rotate(90deg); 
                        border-radius: 25%;
                    }
                    50% { 
                        transform: scale(0.8) rotate(180deg); 
                        border-radius: 75%;
                    }
                    75% { 
                        transform: scale(1.1) rotate(270deg); 
                        border-radius: 10%;
                    }
                }
                
                @keyframes bounce {
                    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
                    40% { transform: translateY(-30px); }
                    60% { transform: translateY(-15px); }
                }
            `}</style>
        </div>
    );
};

export default Home;